/**
 * A phone drawn around a real screenshot: a dark bezel with rounded corners,
 * and (unless left off) the top of a phone screen, the time at the left, the
 * camera island in the middle and signal and battery at the right. The
 * screenshot goes inside, full width.
 *
 * The time reads 2:00 because the warning goes out at the default 2 pm; it
 * and the bars are decoration (hidden from screen readers), the screenshot
 * inside carries the meaning and its own alt text. Colours are fixed (a
 * phone looks the same in the light and dark themes).
 *
 * Used by: the remodelled landing sections (Scrapbook, PlantDoctorV2, ShareV2,
 * WarningsV2).
 *
 * @module PhoneFrame
 */

import React from 'react';
import { BatteryFull, Signal } from 'lucide-react';
import { cn } from '@/lib/utils';

/** What goes in the phone, and how it is drawn. */
interface PhoneFrameProps {
  /** The screen's contents: a screenshot, a notification banner. */
  children: React.ReactNode;
  /** Draws the status bar (time, camera island, signal, battery) above the contents; on by default. */
  statusBar?: boolean;
  /** Extra classes for the bezel (a height cap, for one tall screenshot). */
  className?: string;
}

/**
 * The status bar at the top of a phone's screen: decoration, hidden from screen readers.
 */
export const PhoneStatusBar: React.FC = () => (
  <div className="flex items-center justify-between px-[18px] pb-1 pt-2.5 font-sans text-xs font-semibold leading-none text-[#1c1a17]" aria-hidden="true">
    <span>2:00</span>
    <span className="h-[18px] w-[70px] rounded-full bg-[#1c1a17]" />
    <span className="flex gap-1">
      <Signal className="h-3.5 w-3.5" />
      <BatteryFull className="h-3.5 w-3.5" />
    </span>
  </div>
);

/**
 * A phone around its screen.
 *
 * @param props - See PhoneFrameProps.
 */
export const PhoneFrame: React.FC<PhoneFrameProps> = ({ children, statusBar = true, className }) => (
  <div className={cn('mx-auto w-[280px] overflow-hidden rounded-[2.2rem] border-[6px] border-grow-charcoal bg-grow-charcoal shadow-2xl', className)}>
    <div className="overflow-hidden rounded-[1.8rem] bg-white">
      {statusBar && <PhoneStatusBar />}
      {children}
    </div>
  </div>
);
